import { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

// material-ui
import ButtonBase from '@mui/material/ButtonBase';
import CardContent from '@mui/material/CardContent';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Popper from '@mui/material/Popper';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Divider from '@mui/material/Divider';

// project imports
import Avatar from 'components/@extended/Avatar';
import MainCard from 'components/MainCard';
import Transitions from 'components/@extended/Transitions';
import IconButton from 'components/@extended/IconButton';
import { useAuth } from 'contexts/AuthContext';

// assets
import LogoutOutlined from '@ant-design/icons/LogoutOutlined';
import EditOutlined from '@ant-design/icons/EditOutlined';
import HomeOutlined from '@ant-design/icons/HomeOutlined';
import QuestionCircleOutlined from '@ant-design/icons/QuestionCircleOutlined';
import LockOutlined from '@ant-design/icons/LockOutlined';
import InfoCircleOutlined from '@ant-design/icons/InfoCircleOutlined';
import DollarOutlined from '@ant-design/icons/DollarOutlined';
import CreditCardOutlined from '@ant-design/icons/CreditCardOutlined';

// ==============================|| HEADER CONTENT - PROFILE ||============================== //

export default function Profile() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const anchorRef = useRef(null);
  const [open, setOpen] = useState(false);

  const handleToggle = () => {
    setOpen((prevOpen) => !prevOpen);
  };

  const handleClose = (event) => {
    if (anchorRef.current && anchorRef.current.contains(event.target)) {
      return;
    }
    setOpen(false);
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
    setOpen(false);
  };

  const handleNavigate = (path) => {
    navigate(path);
    setOpen(false);
  };

  // Get user initials for avatar
  const getInitials = (firstName, lastName) => {
    const first = firstName?.[0] || '';
    const last = lastName?.[0] || '';
    return (first + last).toUpperCase() || 'U';
  };

  const fullName = [user?.firstName, user?.lastName].filter(Boolean).join(' ') || user?.email || 'User';
  const orgName = user?.organizationName || user?.organization?.orgName || '';

  return (
    <Box sx={{ flexShrink: 0, ml: 'auto' }}>
      <Tooltip title="Profile" disableInteractive>
        <ButtonBase
          sx={(theme) => ({
            p: 0.25,
            borderRadius: 1,
            '&:focus-visible': { outline: `2px solid ${theme.vars.palette.secondary.dark}`, outlineOffset: 2 }
          })}
          aria-label="open profile"
          ref={anchorRef}
          aria-controls={open ? 'profile-grow' : undefined}
          aria-haspopup="true"
          onClick={handleToggle}
        >
          <Avatar 
            alt={fullName} 
            src={user?.avatar || undefined}
            size="sm" 
            sx={{ 
              bgcolor: 'primary.main',
              color: 'white',
              fontSize: '0.875rem',
              fontWeight: 600,
              '&:hover': { outline: '1px solid', outlineColor: 'primary.main' } 
            }}
          >
            {getInitials(user?.firstName, user?.lastName)}
          </Avatar>
        </ButtonBase>
      </Tooltip>
      <Popper
        placement="bottom-end"
        open={open}
        anchorEl={anchorRef.current}
        role={undefined}
        transition
        disablePortal
        popperOptions={{
          modifiers: [
            {
              name: 'offset',
              options: {
                offset: [0, 9]
              }
            }
          ]
        }}
      >
        {({ TransitionProps }) => (
          <Transitions type="grow" position="top-right" in={open} {...TransitionProps}>
            <Paper sx={(theme) => ({ boxShadow: theme.vars.customShadows.z1, width: 290, minWidth: 240, maxWidth: { xs: 250, md: 290 } })}>
              <ClickAwayListener onClickAway={handleClose}>
                <MainCard elevation={0} border={false} content={false}>
                  <CardContent sx={{ px: 2.5, pt: 3 }}>
                    <Grid container sx={{ justifyContent: 'space-between', alignItems: 'center' }}>
                      <Grid>
                        <Stack direction="row" sx={{ gap: 1.25, alignItems: 'center' }}>
                          <Avatar 
                            alt={fullName} 
                            src={user?.avatar || undefined}
                            sx={{ width: 32, height: 32, bgcolor: 'primary.main', color: 'white', fontWeight: 600 }}
                          >
                            {getInitials(user?.firstName, user?.lastName)}
                          </Avatar>
                          <Stack>
                            <Typography variant="h6">{fullName}</Typography>
                            {orgName && (
                              <Typography variant="body2" color="text.secondary">
                                {orgName}
                              </Typography>
                            )}
                          </Stack>
                        </Stack>
                      </Grid>
                      <Grid>
                        <Tooltip title="Logout">
                          <IconButton size="large" sx={{ color: 'text.primary' }} onClick={handleLogout}>
                            <LogoutOutlined />
                          </IconButton>
                        </Tooltip>
                      </Grid>
                    </Grid>
                  </CardContent>

                  <Divider />

                  <List component="nav" sx={{ p: 0, '& .MuiListItemIcon-root': { minWidth: 32 } }}>
                    <ListItemButton onClick={() => handleNavigate('/dashboard/default')}>
                      <ListItemIcon>
                        <HomeOutlined />
                      </ListItemIcon>
                      <ListItemText primary="Home" />
                    </ListItemButton>
                    <ListItemButton onClick={() => handleNavigate('/profile/edit')}>
                      <ListItemIcon>
                        <EditOutlined />
                      </ListItemIcon>
                      <ListItemText primary="Edit Profile" />
                    </ListItemButton>
                    {(user?.role === 'SUPER_ADMIN' || user?.role === 'ORG_ADMIN') && (
                      <>
                        <ListItemButton onClick={() => handleNavigate('/subscription/pricing')}>
                          <ListItemIcon>
                            <DollarOutlined />
                          </ListItemIcon>
                          <ListItemText primary="Pricing" />
                        </ListItemButton>
                        <ListItemButton onClick={() => handleNavigate('/subscription/billing')}>
                          <ListItemIcon>
                            <CreditCardOutlined />
                          </ListItemIcon>
                          <ListItemText primary="Billing" />
                        </ListItemButton>
                      </>
                    )}
                    
                    <Divider sx={{ my: 1 }} />

                    <ListItemButton onClick={() => handleNavigate('/about')}>
                      <ListItemIcon>
                        <InfoCircleOutlined />
                      </ListItemIcon>
                      <ListItemText primary="About Us" />
                    </ListItemButton>
                    <ListItemButton onClick={() => handleNavigate('/privacy')}>
                      <ListItemIcon>
                        <LockOutlined />
                      </ListItemIcon>
                      <ListItemText primary="Privacy Policy" />
                    </ListItemButton>
                    <ListItemButton onClick={() => handleNavigate('/terms')}>
                      <ListItemIcon>
                        <QuestionCircleOutlined />
                      </ListItemIcon>
                      <ListItemText primary="Terms of Service" />
                    </ListItemButton>
                    
                    <Divider sx={{ my: 1 }} />
                    
                    <ListItemButton onClick={handleLogout}>
                      <ListItemIcon>
                        <LogoutOutlined />
                      </ListItemIcon>
                      <ListItemText primary="Logout" />
                    </ListItemButton>
                  </List>
                </MainCard>
              </ClickAwayListener>
            </Paper>
          </Transitions>
        )}
      </Popper>
    </Box>
  );
}
